"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { ProductItem, getUserProducts, deleteImageFromStorage, updateProduct } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Package, X, Pencil } from '@/components/icons';
import { useToast } from '@/components/ui/use-toast';
import { useSession } from '@/context/SessionContext';
import ProductCard from '@/components/ProductCard';
import { Link } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';

const MyProductListings: React.FC = () => {
  const { session, loading: sessionLoading } = useSession();
  const [myProducts, setMyProducts] = useState<ProductItem[]>([]);
  const [loadingMyProducts, setLoadingMyProducts] = useState(true);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const { toast } = useToast();

  const currentUserId = session?.user?.id;

  const fetchMyProducts = useCallback(async () => {
    if (!currentUserId) {
      setMyProducts([]);
      setLoadingMyProducts(false);
      return;
    }
    setLoadingMyProducts(true);
    try {
      const fetchedProducts = await getUserProducts(currentUserId);
      setMyProducts(fetchedProducts);
    } catch (error) {
      console.error("Error fetching user products:", error);
      toast({
        title: "Error al cargar tus productos",
        description: "No se pudieron cargar tus publicaciones. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setLoadingMyProducts(false);
    }
  }, [currentUserId, toast]);

  useEffect(() => {
    if (!sessionLoading) {
      fetchMyProducts();
    }
  }, [sessionLoading, fetchMyProducts]);

  const handleProductUpdate = useCallback((updatedProduct: ProductItem) => {
    setMyProducts(prevProducts =>
      prevProducts.map(p => (p.id === updatedProduct.id ? updatedProduct : p))
    );
  }, []);

  const handleDeleteProduct = async (productId: string) => {
    if (!currentUserId) {
      toast({ title: "Error", description: "Debes iniciar sesión para eliminar productos.", variant: "destructive" });
      return;
    }
    setDeletingProductId(productId);
    try {
      // Fetch product to get image URLs before deleting
      const productToDelete = myProducts.find(p => p.id === productId);
      if (productToDelete) {
        // Delete images from storage
        for (const url of productToDelete.image_urls || []) {
          await deleteImageFromStorage(url);
        }
        if (productToDelete.cover_url && !productToDelete.image_urls?.includes(productToDelete.cover_url)) {
          await deleteImageFromStorage(productToDelete.cover_url);
        }
      }

      // Update product status to 'deleted' instead of actual deletion
      await updateProduct(productId, currentUserId, { status: 'deleted' });

      toast({
        title: "Producto eliminado",
        description: "Tu producto ha sido marcado como eliminado.",
      });
      fetchMyProducts(); // Refresh the list
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Error al eliminar producto",
        description: "No se pudo eliminar el producto. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setDeletingProductId(null);
    }
  };

  if (sessionLoading) {
    return (
      <div className="space-y-6 w-full -mx-4 px-4 md:mx-0 md:px-0">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-60 w-full" />
      </div>
    );
  }

  if (!currentUserId) {
    return (
      <p className="text-muted-foreground text-center p-8">
        Inicia sesión para ver tus publicaciones.
      </p>
    );
  }

  return (
    <div className="space-y-6 w-full -mx-4 px-4 md:mx-0 md:px-0">
      {loadingMyProducts ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-72 w-full" />
          ))}
        </div>
      ) : myProducts.length === 0 ? (
        <p className="text-muted-foreground text-center p-8">
          Aún no tienes productos publicados. ¡Anímate a listar uno!
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {myProducts.map((product) => {
            if (!product || !product.id || product.status === 'deleted') {
              return null;
            }
            return (
              <Card key={product.id} className="flex flex-col overflow-hidden rounded-lg shadow-md">
                <ProductCard product={product} currentUserId={currentUserId} session={session} onProductUpdate={handleProductUpdate} />
                <div className="flex justify-end gap-2 p-4 border-t">
                  <Link to={`/edit-product/${product.id}`}>
                    <Button variant="outline" size="sm">
                      <Pencil className="h-4 w-4 mr-2" /> Editar
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" disabled={deletingProductId === product.id}>
                        {deletingProductId === product.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4 mr-2" />
                        )}
                        Eliminar
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent
                      aria-labelledby={`delete-product-title-${product.id}`}
                      aria-describedby={`delete-product-description-${product.id}`}
                    >
                      <AlertDialogHeader>
                        <AlertDialogTitle id={`delete-product-title-${product.id}`}>¿Estás seguro de que quieres eliminar este producto?</AlertDialogTitle>
                        <AlertDialogDescription id={`delete-product-description-${product.id}`}>
                          Esta acción marcará el producto como eliminado y ya no será visible en Tiendas. Las imágenes asociadas también serán eliminadas.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteProduct(product.id)}>
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyProductListings;